/**
 * Read an image's pixel dimensions from its FILE HEADER, without decoding it.
 *
 * The point is to answer "how many pixels is this?" before anything allocates
 * them. A byte-size limit does not bound pixels: PNG and WebP are lossless, so a
 * few hundred KB can describe 30000×30000 — 900 megapixels, ~3.6GB once decoded
 * to RGBA. `createImageBitmap` decodes in full before any crop or resize can
 * apply, so a check that happens after it has already lost.
 *
 * Only the four formats the avatar upload accepts are handled
 * (`AVATAR_EXTENSIONS` server-side). Anything else — or a truncated or
 * unrecognised header — returns null, which callers must treat as "unknown, let
 * it through": refusing an upload because a header could not be parsed trades a
 * rare resource problem for a common broken feature, and the byte cap still
 * applies either way.
 */
export type ImageFormat = 'png' | 'gif' | 'jpeg' | 'webp'

/**
 * What a probe can conclude, and the three cases are genuinely different:
 *
 * - `sized`   — dimensions known; the caller can compare them to a budget.
 * - `unsized` — the magic bytes say it IS one of these formats, but the
 *               dimensions could not be read. The caller must NOT let this
 *               through: we know what it is and cannot vouch for its size, so
 *               allowing it is exactly the bypass the guard exists to stop.
 * - `unknown` — the magic bytes match nothing we parse. Safe to let through:
 *               the server's own allowlist accepts only these four formats, so
 *               anything unrecognised here is rejected there anyway.
 *
 * Collapsing `unsized` into `unknown` — which an earlier version did, by
 * returning null for both — is what let a JPEG with an oversized metadata block
 * skip the check entirely.
 */
export type ImageProbe =
  | { kind: 'sized'; width: number; height: number }
  | { kind: 'unsized'; format: ImageFormat }
  | { kind: 'unknown' }

export interface ImageDimensions {
  width: number
  height: number
}

/**
 * The first `bytes` of `blob`, via FileReader rather than `Blob.arrayBuffer()`.
 *
 * Both are universally supported in browsers, but jsdom does not implement
 * `Blob.arrayBuffer`, so using it would make this module untestable — and an
 * untested byte parser is the last thing worth shipping.
 */
function readHead(blob: Blob, bytes: number): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'))
    reader.readAsArrayBuffer(blob.slice(0, bytes))
  })
}

const ascii = (view: DataView, offset: number, length: number): string => {
  let out = ''
  for (let i = 0; i < length; i += 1) out += String.fromCharCode(view.getUint8(offset + i))
  return out
}

/** PNG: 8-byte signature, then IHDR's width/height as big-endian u32. */
function pngDimensions(view: DataView): ImageDimensions | null {
  if (view.byteLength < 24) return null
  if (view.getUint32(0) !== 0x89504e47) return null
  return { width: view.getUint32(16), height: view.getUint32(20) }
}

/**
 * GIF: the LARGER of the logical screen and the first frame's own bounds.
 *
 * The logical screen descriptor alone is not a bound. A frame carries its own
 * width/height and a malformed file can declare one far bigger than the canvas —
 * and since every GIF dimension is a u16, the ceiling is 65535×65535, which is
 * 4.29 gigapixels and ~17GB as RGBA. Reading only the screen descriptor let
 * exactly that through.
 *
 * Reaching the frame means stepping over the global colour table (its size is
 * encoded in the packed field) and any extension blocks, each a chain of
 * length-prefixed sub-blocks. The walk is bounded by the buffer and by an
 * iteration cap, so a malformed chain cannot spin.
 */
function gifDimensions(view: DataView): ImageDimensions | null {
  if (view.byteLength < 13) return null
  const screen = { width: view.getUint16(6, true), height: view.getUint16(8, true) }

  const packed = view.getUint8(10)
  const hasGlobalColorTable = (packed & 0x80) !== 0
  // 3 bytes per entry, 2^(N+1) entries.
  let offset = 13 + (hasGlobalColorTable ? 3 * 2 ** ((packed & 0x07) + 1) : 0)

  for (let guard = 0; guard < 1000 && offset < view.byteLength; guard += 1) {
    const block = view.getUint8(offset)
    if (block === 0x2c) {
      // Image descriptor: left, top, width, height — all u16 LE.
      if (offset + 9 > view.byteLength) break
      const frame = {
        width: view.getUint16(offset + 5, true),
        height: view.getUint16(offset + 7, true),
      }
      return {
        width: Math.max(screen.width, frame.width),
        height: Math.max(screen.height, frame.height),
      }
    }
    if (block === 0x21) {
      // Extension: introducer, label, then a sub-block chain ending in 0x00.
      offset += 2
      while (offset < view.byteLength) {
        const size = view.getUint8(offset)
        offset += 1 + size
        if (size === 0) break
      }
      continue
    }
    // Trailer (0x3b) or anything unexpected: stop and use what we have.
    break
  }

  // No frame found in the window — the canvas is the only bound we have, and it
  // is still a real answer for a well-formed single-frame GIF.
  return screen
}

/**
 * JPEG: walk the segment chain to the first Start-Of-Frame.
 *
 * Dimensions are not at a fixed offset — they live in an SOFn segment that sits
 * after a variable number of others (EXIF, ICC, comments), so the markers have
 * to be walked. `FFC4`/`FFC8`/`FFCC` are excluded because they share the `FFCx`
 * range without being frame headers.
 */
function jpegDimensions(view: DataView): ImageDimensions | null {
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null
  let offset = 2
  while (offset + 9 < view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      offset += 1
      continue
    }
    const marker = view.getUint8(offset + 1)
    const isFrameHeader =
      marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc
    if (isFrameHeader) {
      return { height: view.getUint16(offset + 5), width: view.getUint16(offset + 7) }
    }
    // Markers without a payload; anything else carries a big-endian length.
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      offset += 2
    } else {
      offset += 2 + view.getUint16(offset + 2)
    }
  }
  return null
}

/**
 * WebP: three sub-formats, three encodings, all needed.
 *
 * A browser re-encode emits VP8L (lossless), a camera export is usually VP8, and
 * anything with alpha or animation is VP8X — so handling only one of them would
 * silently skip the check for most real files.
 */
function webpDimensions(view: DataView): ImageDimensions | null {
  if (view.byteLength < 30) return null
  if (ascii(view, 0, 4) !== 'RIFF' || ascii(view, 8, 4) !== 'WEBP') return null
  const chunk = ascii(view, 12, 4)

  if (chunk === 'VP8 ') {
    // 14-bit dimensions after the 3-byte start code and the 2-byte sync word.
    return {
      width: view.getUint16(26, true) & 0x3fff,
      height: view.getUint16(28, true) & 0x3fff,
    }
  }
  if (chunk === 'VP8L') {
    // 14 bits of (width-1) then 14 bits of (height-1), packed little-endian
    // after a 1-byte signature.
    const bits = view.getUint32(21, true)
    return { width: (bits & 0x3fff) + 1, height: ((bits >> 14) & 0x3fff) + 1 }
  }
  if (chunk === 'VP8X') {
    // Canvas size as two 24-bit little-endian (value - 1) fields.
    const read24 = (o: number) =>
      view.getUint8(o) | (view.getUint8(o + 1) << 8) | (view.getUint8(o + 2) << 16)
    return { width: read24(24) + 1, height: read24(27) + 1 }
  }
  return null
}

/**
 * Dimensions of `file`, or null when they cannot be determined.
 *
 * Reads the first 1MB. Every format above carries its dimensions in the first
 * few dozen bytes except JPEG, whose SOF sits behind however much APPn metadata
 * the encoder wrote — a single EXIF segment is capped at 64KB but there can be
 * several, and an embedded ICC profile runs to megabytes. 64KB was not enough:
 * a JPEG whose frame header sat past it read as "no dimensions" and, under the
 * old fail-open rule, skipped the check completely.
 *
 * 1MB is a bound, not a guarantee, which is why exceeding it now reports
 * `unsized` rather than `unknown` — a JPEG we cannot size is refused instead of
 * waved through. Reading the whole file would defeat the point of not
 * allocating it.
 */
export async function probeImage(file: Blob): Promise<ImageProbe> {
  let view: DataView
  try {
    view = new DataView(await readHead(file, 1024 * 1024))
  } catch {
    return { kind: 'unknown' }
  }

  const format = detectFormat(view)
  if (!format) return { kind: 'unknown' }

  const dims = READERS[format](view)
  if (!dims || dims.width <= 0 || dims.height <= 0) return { kind: 'unsized', format }
  return { kind: 'sized', width: dims.width, height: dims.height }
}

const READERS: Record<ImageFormat, (view: DataView) => ImageDimensions | null> = {
  png: pngDimensions,
  gif: gifDimensions,
  jpeg: jpegDimensions,
  webp: webpDimensions,
}

/**
 * Which format the BYTES say this is — never the filename or the declared
 * mimetype, both of which the caller controls.
 */
function detectFormat(view: DataView): ImageFormat | null {
  if (view.byteLength >= 8 && view.getUint32(0) === 0x89504e47) return 'png'
  if (view.byteLength >= 6) {
    const sig = ascii(view, 0, 6)
    if (sig === 'GIF87a' || sig === 'GIF89a') return 'gif'
  }
  if (view.byteLength >= 12 && ascii(view, 0, 4) === 'RIFF' && ascii(view, 8, 4) === 'WEBP') {
    return 'webp'
  }
  if (view.byteLength >= 2 && view.getUint16(0) === 0xffd8) return 'jpeg'
  return null
}
